'use client';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { getBaseImageUrl, getMerchantHref } from '@/constants/hooks';

interface Props {
    merchants: any[];
    companyDomain: string;
    mer_slug: string;
    mer_slug_type: string;
}

const RoundedMerchantSlider: React.FC<Props> = ({ merchants, companyDomain, mer_slug, mer_slug_type }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef(0);
    const scrollStart = useRef(0);

    const syncState = useCallback(() => {
        const t = trackRef.current;
        if (!t) return;
        setCanPrev(t.scrollLeft > 4);
        setCanNext(t.scrollLeft < t.scrollWidth - t.clientWidth - 4);
    }, []);

    useEffect(() => {
        const t = trackRef.current;
        if (!t) return;
        syncState();
        t.addEventListener('scroll', syncState, { passive: true });
        window.addEventListener('resize', syncState);
        return () => { t.removeEventListener('scroll', syncState); window.removeEventListener('resize', syncState); };
    }, [merchants, syncState]);

    const scroll = (dir: 'left' | 'right') => {
        if (!trackRef.current) return;
        trackRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
    };

    const onPointerDown = (e: React.PointerEvent) => {
        setIsDragging(true);
        dragStart.current = e.clientX;
        scrollStart.current = trackRef.current?.scrollLeft ?? 0;
        trackRef.current?.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: React.PointerEvent) => {
        if (!isDragging || !trackRef.current) return;
        trackRef.current.scrollLeft = scrollStart.current - (e.clientX - dragStart.current);
    };
    const onPointerUp = () => setIsDragging(false);

    if (!merchants?.length) return null;

    return (
        <div className="relative select-none">
            {/* Glassmorphic Prev arrow */}
            <button
                onClick={() => scroll('left')}
                disabled={!canPrev}
                aria-label="Scroll left"
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-9 h-9 flex items-center justify-center transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 focus:outline-none"
                style={{
                    background: 'rgba(8,28,17,0.7)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(122,252,32,0.4)',
                    clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                    boxShadow: '0 0 16px rgba(122,252,32,0.2)',
                }}
            >
                <svg className="w-3.5 h-3.5" style={{ color: '#7AFC20' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Glassmorphic Next arrow */}
            <button
                onClick={() => scroll('right')}
                disabled={!canNext}
                aria-label="Scroll right"
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-9 h-9 flex items-center justify-center transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none hover:scale-110 focus:outline-none"
                style={{
                    background: 'rgba(8,28,17,0.7)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(122,252,32,0.4)',
                    clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                    boxShadow: '0 0 16px rgba(122,252,32,0.2)',
                }}
            >
                <svg className="w-3.5 h-3.5" style={{ color: '#7AFC20' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Left edge fade */}
            <div className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none transition-opacity duration-300"
                style={{ background: 'linear-gradient(90deg, #081C11, transparent)', opacity: canPrev ? 1 : 0 }} />
            {/* Right edge fade */}
            <div className="absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none transition-opacity duration-300"
                style={{ background: 'linear-gradient(270deg, #081C11, transparent)', opacity: canNext ? 1 : 0 }} />

            {/* Track */}
            <div
                ref={trackRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className="flex gap-3 overflow-x-auto py-2 px-1"
                style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    scrollSnapType: 'x mandatory',
                }}
            >
                <style>{`.rms-track::-webkit-scrollbar{display:none}`}</style>

                {merchants.map((merchant, i) => {
                    const href = getMerchantHref(merchant, mer_slug, mer_slug_type);
                    const firstLetter = merchant.merchant_name?.[0]?.toUpperCase() || '?';

                    return (
                        <Link
                            key={i}
                            href={href}
                            className="group relative flex-shrink-0 flex flex-col items-center gap-2.5 p-4 no-underline overflow-hidden transition-all duration-400 hover:-translate-y-1.5"
                            style={{
                                scrollSnapAlign: 'start',
                                width: '120px',
                                background: 'rgba(245,245,220,0.04)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                border: '1px solid rgba(245,245,220,0.08)',
                                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                            }}
                        >
                            {/* Hover fill */}
                            <div
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
                                style={{
                                    background: 'rgba(122,252,32,0.07)',
                                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                                }}
                            />
                            {/* Hover border glow */}
                            <div
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
                                style={{
                                    boxShadow: 'inset 0 0 0 1px rgba(122,252,32,0.4)',
                                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                                }}
                            />

                            {/* Logo pill */}
                            <div
                                className="relative w-14 h-14 flex items-center justify-center overflow-hidden flex-shrink-0 transition-transform duration-400 group-hover:scale-110"
                                style={{
                                    background: 'rgba(245,245,220,0.95)',
                                    borderRadius: '10px',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
                                }}
                            >
                                {merchant.merchant_logo ? (
                                    <Image
                                        src={getBaseImageUrl(companyDomain, merchant.merchant_logo, '')}
                                        alt={merchant.merchant_name || 'Merchant'}
                                        fill
                                        className="object-contain p-2"
                                        sizes="56px"
                                    />
                                ) : (
                                    <span className="text-lg font-black" style={{ color: '#081C11' }}>{firstLetter}</span>
                                )}
                            </div>

                            {/* Name */}
                            <span
                                className="relative z-10 text-[9px] font-black uppercase tracking-[0.12em] text-center leading-tight line-clamp-2 transition-colors duration-300 group-hover:text-[#7AFC20]"
                                style={{ color: 'rgba(245,245,220,0.55)' }}
                            >
                                {merchant.merchant_name}
                            </span>

                            {/* Offer count */}
                            {merchant.total_offers > 0 && (
                                <span
                                    className="relative z-10 text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 transition-all duration-300 group-hover:text-[#7AFC20] group-hover:border-[rgba(122,252,32,0.3)]"
                                    style={{
                                        background: 'rgba(122,252,32,0.06)',
                                        border: '1px solid rgba(122,252,32,0.12)',
                                        color: 'rgba(122,252,32,0.4)',
                                    }}
                                >
                                    {merchant.total_offers} offers
                                </span>
                            )}

                            {/* Bottom accent */}
                            <div
                                className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                                style={{ background: 'linear-gradient(90deg, transparent, #7AFC20, transparent)' }}
                            />
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default RoundedMerchantSlider;
